import { AlertProps, Button, Card, DropdownProps, Flex, Space, Table, Typography, theme } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { FC, RefObject } from 'react';
import { CheckCircleFill, ChevronDown, ChevronUp, FileEarmarkRuled, XCircleFill } from 'react-bootstrap-icons';

import { AppModal, EmptyText } from '@/common/components';
import { AppModalControlProps } from '@/common/interfaces';

import { SearchConsoleAddressChangingLogs, SearchConsoleAddressChangingStatus } from '@/modules/Domains';
import { Site } from '@/modules/Sites';

import { AddressChangerBadgeAlert, AddressChangerBadgeDropdown } from './Parts';

interface Props {
  isAutoRedirecting: boolean;
  isLoading: boolean;
  redirectingInfo: NonNullable<Site['searchConsoleAddressChanging']> | undefined;
  dropdownWrapperRef: RefObject<HTMLDivElement>;
  badgeRef: RefObject<HTMLDivElement>;
  isAddingRedirectingTaskLoading: boolean;
  logsModalControl: AppModalControlProps;
  handleAutoRedirectingChange: (e: CheckboxChangeEvent) => void;
  isDropdownOpen: boolean;
  handleDropdownOpen: NonNullable<DropdownProps['onOpenChange']>;
  handleOpenLogsModal: () => void;
  handleAddRedirectingTask: () => void;
}

export const AddressChangerBadgeView: FC<Props> = ({
  isAutoRedirecting,
  isLoading,
  redirectingInfo,
  badgeRef,
  dropdownWrapperRef,
  logsModalControl,
  isDropdownOpen,
  isAddingRedirectingTaskLoading,
  handleAddRedirectingTask,
  handleOpenLogsModal,
  handleAutoRedirectingChange,
  handleDropdownOpen,
}) => {
  const { token } = theme.useToken();

  if (isLoading) {
    return <div>Loading</div>;
  } else {
    const alertType: AlertProps['type'] = (() => {
      if (redirectingInfo?.changingStatus === SearchConsoleAddressChangingStatus.Fail) {
        return 'error';
      }

      if (redirectingInfo?.changingStatus === SearchConsoleAddressChangingStatus.Pending) {
        return 'warning';
      }

      if (redirectingInfo?.changingStatus === SearchConsoleAddressChangingStatus.Redirecting) {
        return 'info';
      }

      if (redirectingInfo?.changingStatus === SearchConsoleAddressChangingStatus.Success) {
        return 'success';
      }

      return undefined;
    })();

    return (
      <>
        <div ref={ badgeRef }>
          <AddressChangerBadgeDropdown
            dropdownWrapperRef={ dropdownWrapperRef }
            alertType={ alertType }
            settingValue={ isAutoRedirecting }
            alert={
              <AddressChangerBadgeAlert
                alertType={ alertType }
                redirectingInfo={ redirectingInfo }
                dropdownButton={
                  <Button
                    type="text"
                    size="small"
                    style={{ marginLeft: 'auto' }}
                    onClick={ () => handleDropdownOpen(!isDropdownOpen, { source: 'trigger' }) }
                    icon={ isDropdownOpen ? <ChevronUp/> : <ChevronDown/> }
                  />
                }
                logsButton={
                  <Button
                    type="text"
                    size="small"
                    icon={ <FileEarmarkRuled/> }
                    onClick={ handleOpenLogsModal }
                  />
                }
                isAddingRedirectingTaskLoading={ isAddingRedirectingTaskLoading }
                handleAddRedirectingTask={ handleAddRedirectingTask }
              />
            }
            handleAutoRedirectingChange={ handleAutoRedirectingChange }
            isDropdownOpen={ isDropdownOpen }
            onDropdownOpen={ handleDropdownOpen }
          />
        </div>

        <AppModal
          width={ 800 }
          title={ 'Результат останньої переадресації' }
          controls={ logsModalControl }
          footer={ null }
          closable
        >
          <Card
            size="small"
            style={{ marginBottom: 16, }}
          >
            <Flex align="center">
              {
                redirectingInfo?.changingStatus === SearchConsoleAddressChangingStatus.Success &&
                <Space>
                  <CheckCircleFill color={ token.colorSuccess }/>
                  <span>Процес завершено без критичних помилок</span>
                </Space>
              }
              {
                redirectingInfo?.changingStatus === SearchConsoleAddressChangingStatus.Fail &&
                  <Space>
                    <XCircleFill color={ token.colorError }/>
                    <span>{ redirectingInfo?.changingError }</span>
                  </Space>
              }
            </Flex>
          </Card>

          <Table<SearchConsoleAddressChangingLogs>
            columns={ [
              {
                key: 'domain',
                dataIndex: 'domain',
                title: 'SiteUrl',
                render: (domain) => <Typography.Paragraph
                  style={{ marginBottom: 0, color: 'inherit', fontSize: 'inherit', maxWidth: 'inherit', textOverflow: 'inherit', overflow: 'inherit' }}
                  ellipsis={{
                    rows: 1,
                    tooltip: false
                  }}
                >
                  { domain.siteUrl }
                </Typography.Paragraph>
              },
              {
                key: 'errorMessage',
                dataIndex: 'errorMessage',
                title: 'Помилка',
                render: (errorMessage) => <>
                  {
                    errorMessage
                      ? <Typography.Text style={{ fontSize: 12 }} type="danger">
                        { errorMessage }
                      </Typography.Text>
                      : <EmptyText/>
                  }
                </>
              },
              {
                key: 'makesRedirects',
                dataIndex: 'makesRedirects',
                title: <>Адресу змінено</>,
                width: 140,
                render: (makesRedirects) => <>
                  <Flex align="center" justify="center">
                    {
                      makesRedirects
                        ? <CheckCircleFill size={ 20 } color={ token.colorSuccess }/>
                        : <XCircleFill size={ 20 } color={ token.colorError }/>
                    }
                  </Flex>
                </>
              },
            ] }
            dataSource={ redirectingInfo?.changingLogs ?? [] }
            pagination={ false }
            size="small"
            bordered
          />
        </AppModal>
      </>
    );
  }
};
